<template>
  <UlDialog
    v-model="dialog"
    :show-close="isRetryable || isRetrying"
    :title="internalDialogTitle"
    :left-header-icon="payment?.statusName !== 'payment_confirmed' ? leftHeaderIcon : ''"
    :left-header-icon-type="payment?.statusName !== 'payment_confirmed' ? 'static' : 'animated'"
    :left-header-icon-component="payment?.statusName !== 'payment_confirmed' ? '' : 'Confetti'"
    :buttons="internalDialogButtons"
    :loading="internalDialogLoading"
    :header-type="internalHeaderType"
    :title-bottom-margin="
      !(payment && question && $te(`payment.${payment.statusName}.subtitle`, $i18n.locale))
        ? ''
        : null
    "
    @close="closeDialog"
  >
    <template #text>
      <div v-if="payment && question && !isRetrying">
        <div v-if="$te(`payment.${payment.statusName}.subtitle`, $i18n.locale)">
          {{ $t(`payment.${payment.statusName}.subtitle`) }}
          <i18n
            path="appointments.thankYou.subtitle"
            tag="span"
            class="text-body-small grey-darken-1--text"
          >
            <template #language>
              {{ $t(`appointments.thankYou.${$auth.user.language}`) }}
            </template>
          </i18n>
        </div>
      </div>
      <GoPay v-else-if="isRetrying" @close="closeDialog" />
      <div v-else></div>
    </template>
  </UlDialog>
</template>

<script>
import { mapGetters } from 'vuex';
import goPay from '@/mixins/goPay';
import UlDialog from '@/components/Atoms/UlDialog';
import GoPay from '@/components/Molecules/GoPay';
import ecommerceGtm from '@/mixins/ecommerceGtm';

export default {
  name: 'ProfileGoPayThankYouDialog',
  components: { GoPay, UlDialog },
  mixins: [goPay, ecommerceGtm],
  data() {
    return {
      question: null,
      payment: {},
      showError: true,
      createdGtmSent: false,
      checkPaymentTimeout: 0,
      isRetrying: false,
      loading: true
    };
  },
  computed: {
    ...mapGetters({
      dialogTitle: 'profile/goPay/dialogTitle',
      dialogButtons: 'profile/goPay/dialogButtons',
      dialogLeftHeaderIcon: 'profile/goPay/dialogLeftHeaderIcon',
      dialogHeaderType: 'profile/goPay/dialogHeaderType',
      goPayId: 'profile/goPay/goPayId'
    }),
    dialogLoading: {
      get() {
        return this.$store.state.profile.goPay.dialogLoading;
      },
      set(value) {
        this.$store.commit('profile/goPay/setDialogLoading', value);
      }
    },
    internalDialogTitle() {
      if (this.isRetrying) {
        return this.dialogTitle;
      }
      if (this.payment && this.question) {
        return this.$te(`payment.${this.payment.statusName}.title`, this.$i18n.locale)
          ? this.$t(`payment.${this.payment.statusName}.title`)
          : this.$t(`payment.${this.payment.statusName}`);
      }
      return '';
    },
    internalDialogLoading() {
      if (this.isRetrying) {
        return this.dialogLoading;
      }
      return this.loading;
    },
    leftHeaderIcon() {
      if (this.isRetrying) {
        return this.dialogLeftHeaderIcon;
      }
      return this.paymentIcons ? `icons/${this.paymentIcons}.svg` : null;
    },
    internalHeaderType() {
      if (this.isRetrying) {
        return this.dialogHeaderType;
      }
      return 'information';
    },
    isRetryable() {
      return [
        'payment_canceled',
        'payment_rejected',
        'payment_create_error',
        'payment_timeouted'
      ].includes(this.payment.statusName);
    },
    paymentIcons() {
      switch (this.payment.statusName) {
        case 'payment_created':
        case 'payment_wait':
          return 'clock';
        case 'payment_canceled':
        case 'payment_rejected':
        case 'payment_create_error':
        case 'payment_timeouted':
          return 'person-sad';
        case 'payment_in_progress':
          return 'clock';
        case 'payment_refunded':
          return 'payment';
        case 'payment_confirmed':
          return 'confetti';
        default:
          return null;
      }
    },
    ...mapGetters({
      paymentMethodName: 'profile/dashboard/paymentMethodName'
    }),
    dialog: {
      get() {
        const dialogVisible = this.$store.state.profile.dashboard.dialog === 'gopayThankYou';
        return dialogVisible;
      },
      set(value) {
        if (value) {
          this.$store.commit('profile/dashboard/setDialog', 'gopayThankYou');
        } else {
          this.$store.commit('profile/dashboard/setDialog', false);
        }
      }
    },
    internalDialogButtons() {
      if (this.isRetrying) {
        return this.dialogButtons;
      }
      if (this.isRetryable) {
        return [
          {
            props: {
              color: 'tetrary'
            },
            events: {
              click: () => {
                this.startNow();
              }
            },
            text: this.$t('close')
          },
          {
            events: {
              click: () => {
                this.setIdentifier(this.question.hashId);
                this.getAvailableTariffs();
                this.isRetrying = true;
              }
            },
            text: this.$t('tryAgain')
          }
        ];
      }
      if (this.payment.statusName !== 'payment_in_progress') {
        return [
          {
            props: {
              color:
                this.payment.statusName === 'payment_confirmed' ||
                this.payment.statusName === 'payment_refunded'
                  ? 'primary'
                  : 'tetrary'
            },
            events: {
              click: () => {
                this.startNow();
              }
            },
            text: this.$t(this.payment.statusName === 'payment_confirmed' ? 'finish' : 'close')
          }
        ];
      }
      return [];
    }
  },
  watch: {
    goPayId(newValue) {
      if (newValue) {
        this.checkPayment();
      }
    },
    dialog(newValue) {
      if (newValue) {
        this.$store.commit('general/setShowNeedHelp', true);
        this.$store.commit('general/setNeedHelpInverted', true);
        if (!this.goPayId) {
          this.dialogLoading = false;
          this.loading = false;
          this.isRetrying = true;
        } else {
          this.checkPayment();
        }
      } else {
        this.$store.commit('general/setShowNeedHelp', false);
      }
    }
  },
  mounted() {
    if (this.goPayId) {
      this.checkPayment();
    } else {
      this.dialogLoading = false;
      this.loading = false;
      this.isRetrying = true;
    }
  },
  methods: {
    async checkPayment() {
      if (!this.goPayId) {
        this.dialogLoading = false;
        this.loading = false;
        this.isRetrying = true;
        return;
      }

      this.dialogLoading = false;
      this.isRetrying = false;
      this.loading = true;
      clearTimeout(this.checkPaymentTimeout);

      await this.$http
        .get(`/v1/payment/status/byId/${this.goPayId}?providerType=gopay`)
        .then(async data => {
          this.payment = data;
          if (
            ['payment_created', 'payment_in_progress', 'payment_wait'].includes(
              this.payment.statusName
            )
          ) {
            this.checkPaymentTimeout = setTimeout(() => {
              this.checkPayment();
            }, 5000);
          }
          if (this.payment.statusName === 'payment_created') {
            if (!this.createdGtmSent) {
              this.gtmPush({ event: 'waiting_for_payment' });
              this.createdGtmSent = true;
            }
          } else if (this.payment.statusName === 'payment_confirmed') {
            await this.$http
              .get(`/v1/paymentMethods/byQuestionAndCategory/${data.request.identifier}`)
              .then(methodData => {
                this.setBackendPaymentMethodGroups(methodData);
              })
              .catch(err => {
                this.handleError({ error: err, code: '0x00000025' });
              });
            let methodGroup = this.backendPaymentMethodGroups.find(group => {
              return group.methods.find(method => {
                return (
                  method.paymentProviderId === data.paymentProviderId &&
                  method.id === data.paymentMethodId
                );
              });
            });
            this.sendQuestionGtm(
              methodGroup?.methods?.find(method => {
                return (
                  method.paymentProviderId === data.paymentProviderId &&
                  method.id === data.paymentMethodId
                );
              })?.title || '',
              data.request.identifier
            );
          } else {
            this.gtmPush({ event: 'error', error: 'payment' });
          }
          if (data.request.type === 'question') {
            await this.$http
              .get(`/v3/fe/questions/${data.request.identifier}`)
              .then(async questionData => {
                this.question = questionData;
              })
              .catch(err => {
                this.handleError({ error: err, code: '0x00000026' });
              });
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.isRetrying = true;
        });
    },
    closeDialog() {
      window.clearTimeout(this.checkPaymentTimeout);
      this.payment = {};
      this.question = null;
      this.loading = true;
      this.isRetrying = false;
      this.createdGtmSent = false;
      this.$store.commit('profile/goPay/setGoPayId', '');
      this.$store.commit('profile/dashboard/setDialog', false);
    },
    startNow() {
      this.closeDialog();
    }
  }
};
</script>
